import React, { useState } from "react";
import { motion } from "framer-motion";
import { Shield, Lock, Eye, FileText, HelpCircle, ArrowRight } from "lucide-react";
import Navbar from "../components/landing/Navbar";
import SignupDialog from "../components/landing/SignupDialog";

// Soft mesh gradients background matching the STUDLYF style
const PurpleFloatingBlobs = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
    <motion.div
      animate={{ x: [0, 30, -20, 0], y: [0, -20, 20, 0], scale: [1, 1.05, 0.95, 1] }}
      transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-[-5%] left-[-5%] w-[400px] h-[400px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(124, 58, 237, 0.18) 0%, transparent 70%)", filter: "blur(80px)" }}
    />
    <motion.div
      animate={{ x: [0, -20, 20, 0], y: [0, 30, -10, 0], scale: [1, 0.95, 1.05, 1] }}
      transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-[30%] right-[-5%] w-[450px] h-[450px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%)", filter: "blur(85px)" }}
    />
  </div>
);

export default function PrivacyPolicyPage() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");

  const openSignup = () => { setAuthMode("signup"); setSignupOpen(true); };
  const openLogin = () => { setAuthMode("login"); setSignupOpen(true); };

  return (
    <div className="relative min-h-screen flex flex-col flex-1 w-full bg-[#FAFAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 overflow-x-hidden font-sans">
      <Navbar onGetStarted={openSignup} onLogin={openLogin} />
      <PurpleFloatingBlobs />
      
      <main className="relative z-10 pt-28 pb-16 flex-1 w-full px-4 sm:px-5 md:px-8" data-testid="privacy-policy-page">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 dark:bg-gray-900/50 backdrop-blur-md border border-gray-200/50 dark:border-gray-800/50 text-xs font-semibold text-[#7C3AED] dark:text-[#8C85FF] w-fit mx-auto mb-4">
            <Shield className="w-3.5 h-3.5" />
            Security & Trust
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight">
            Privacy <span className="brand-gradient-text">Policy</span>
          </h1>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Last Updated: July 20, 2026 • We respect your privacy and protect your personal data.
          </p>
        </div>

        {/* Content Box */}
        <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl border border-gray-100 dark:border-gray-800/80 rounded-[32px] p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="space-y-8">
            
            {/* 1. Introduction */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <FileText className="w-5 h-5 text-[#7C3AED]" />
                1. Introduction
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Welcome to STUDLYF AI. We are dedicated to providing student builders, startup founders, and mentors with a powerful, AI-driven operating system to validate, build, launch, and scale businesses. Your privacy is of paramount importance to us. This Privacy Policy describes how we collect, use, process, and disclose your information, including personal data, in conjunction with your access to and use of our platform and website.
              </p>
            </section>

            {/* 2. Information We Collect */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Lock className="w-5 h-5 text-[#7C3AED]" />
                2. Information We Collect
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                To provide our services effectively, we collect several categories of information:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 dark:text-gray-300 text-sm space-y-1.5">
                <li><strong>Account Information:</strong> When you register for STUDLYF AI, we collect your full name, email address, password, company or project name, and your role (e.g., Founder, Student, Mentor).</li>
                <li><strong>Workspace Data:</strong> Any information, documents, ideas, strategy papers, or project outlines you input into the AI Workspace are stored and processed to deliver customized AI insights.</li>
                <li><strong>Usage Information:</strong> We collect details about how you interact with our platform, such as features accessed, templates used, and time spent on various modules.</li>
                <li><strong>Communications:</strong> When you contact us via contact forms, email, or chat support, we store those messages to resolve your queries.</li>
              </ul>
            </section>

            {/* 3. How We Use Your Information */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Eye className="w-5 h-5 text-[#7C3AED]" />
                3. How We Use Your Information
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We use the collected information for the following purposes:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 dark:text-gray-300 text-sm space-y-1.5">
                <li>To operate, maintain, and improve the STUDLYF AI platform.</li>
                <li>To personalize your experience, recommending appropriate templates, strategies, and funding resources.</li>
                <li>To facilitate collaborative workspace modules and allow secure authentication.</li>
                <li>To communicate with you regarding feature releases, system updates, newsletter briefings, and community events.</li>
                <li>To comply with legal obligations and enforce our Terms of Service.</li>
              </ul>
            </section>

            {/* 4. Cookies */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <HelpCircle className="w-5 h-5 text-[#7C3AED]" />
                4. Cookies and Tracking Technologies
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our platform and hold certain configuration information. Cookies are files with small amounts of data which may include an anonymous unique identifier. They help us understand your preferences based on previous or current site activity, enabling us to provide you with improved services. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
              </p>
            </section>

            {/* 5. Data Security */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Lock className="w-5 h-5 text-[#7C3AED]" />
                5. Data Security
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We implement a variety of security measures to maintain the safety of your personal information. Your personal data is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems, and are required to keep the information confidential. We offer the use of secure servers, and all sensitive/credit information is transmitted via Secure Socket Layer (SSL) technology.
              </p>
            </section>

            {/* 6. Third-Party Services */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <ArrowRight className="w-5 h-5 text-[#7C3AED]" />
                6. Third-Party Services
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We may share information with trusted third-party service providers (such as hosting providers, authentication services like Google/Supabase, or payment gateways) to help us deliver our platform services. These third parties have access to your personal information only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
              </p>
            </section>

            {/* 7. User Rights */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Shield className="w-5 h-5 text-[#7C3AED]" />
                7. User Rights
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Depending on your jurisdiction, you may have the following rights regarding your personal data:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 dark:text-gray-300 text-sm space-y-1.5">
                <li><strong>Access:</strong> The right to request copies of your personal data.</li>
                <li><strong>Rectification:</strong> The right to request that we correct any information you believe is inaccurate.</li>
                <li><strong>Erasure:</strong> The right to request that we erase your personal data under certain conditions.</li>
                <li><strong>Portability:</strong> The right to request that we transfer the data we have collected to another organization.</li>
              </ul>
            </section>

            {/* 8. Data Retention */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <FileText className="w-5 h-5 text-[#7C3AED]" />
                8. Data Retention
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
              </p>
            </section>

            {/* 9. Contact Information */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <HelpCircle className="w-5 h-5 text-[#7C3AED]" />
                9. Contact Information
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at:
              </p>
              <div className="mt-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-950/40 border border-gray-150 dark:border-gray-800 text-sm">
                <p className="font-semibold text-gray-900 dark:text-white">STUDLYF AI Support Team</p>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Email: <a href="mailto:studlyf21@gmail.com" className="text-[#7C3AED] hover:underline font-semibold">studlyf21@gmail.com</a></p>
                <p className="text-gray-500 dark:text-gray-400">Address: Ecosystem Innovation Lab, India</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <SignupDialog open={signupOpen} onOpenChange={setSignupOpen} initialMode={authMode} />
    </div>
  );
}
